import React, { useState, useEffect } from "react";
import { ArrowDownToLine, ArrowLeft } from "lucide-react";
import { NavLink, useParams } from "react-router-dom";
import { dummyCustomerOutstandingDetails } from "../../../../../dummyData";
import CustomerTransactionsTable from "./CustomerTransactionsTable";

const CustomerDetailView = () => {
  const { customerId } = useParams();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Format currency helper
  const formatCurrency = amount => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  useEffect(() => {
    // Simulate API call to get customer details
    setLoading(true);
    setTimeout(() => {
      const data = dummyCustomerOutstandingDetails[customerId];
      setCustomerData(data);
      setLoading(false);
    }, 600);
  }, [customerId]);

  if (loading) {
    return (
      <div className="bg-white min-h-[calc(100vh-120px)] flex items-center justify-center">
        <div className="animate-pulse text-[#5652B0] font-medium">
          Loading...
        </div>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="bg-white min-h-[calc(100vh-120px)] flex items-center justify-center">
        <div className="text-red-500">Customer not found</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-[calc(100vh-120px)]">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          <NavLink
            to="/report/outstanding-balance"
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </NavLink>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            {customerData.customerInfo.name}
          </h1>
        </div>

        {/* Download Button */}
        <button className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
          <span className="px-4 py-2 text-gray-700 font-medium">Download</span>
          <span className="bg-[#5652B0] py-2 px-3">
            <ArrowDownToLine size={20} className="text-white" />
          </span>
        </button>
      </div>

      {/* Customer Info Section - Structured to match screenshot */}
      <div className="p-6 border-b">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex mb-4">
              <span className="text-gray-500 font-medium w-28">Customer:</span>
              <span className="text-gray-800 font-medium">
                {customerData.customerInfo.name}
              </span>
            </div>
            <div className="flex mb-4">
              <span className="text-gray-500 font-medium w-28">Address:</span>
              <span className="text-gray-800">
                {customerData.customerInfo.address}
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-500 font-medium w-28">Phone:</span>
              <span className="text-gray-800">
                {customerData.customerInfo.phone}
              </span>
            </div>
          </div>

          <div>
            <div className="flex mb-4">
              <span className="text-gray-500 font-medium w-44">Period:</span>
              <span className="text-gray-800">
                {customerData.customerInfo.period.from} to{" "}
                {customerData.customerInfo.period.to}
              </span>
            </div>
            <div className="flex mb-4">
              <span className="text-gray-500 font-medium w-44">
                Opening Balance:
              </span>
              <span className="text-gray-800 font-medium">
                {formatCurrency(customerData.customerInfo.openingBalance)} Dr
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-500 font-medium w-44">
                Current Balance:
              </span>
              <span className="text-[#5652B0] font-semibold">
                {formatCurrency(customerData.totals.totalPending)} Dr
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
        <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-1">
            Total Invoiced
          </h3>
          <p className="text-xl font-semibold text-gray-800">
            {formatCurrency(customerData.totals.totalAmount)}
          </p>
        </div>
        <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Total Paid</h3>
          <p className="text-xl font-semibold text-green-600">
            {formatCurrency(customerData.totals.totalPaid)}
          </p>
        </div>
        <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-1">
            Outstanding Balance
          </h3>
          <p className="text-xl font-semibold text-[#5652B0]">
            {formatCurrency(customerData.totals.totalPending)}
          </p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="px-6 pb-10">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Transaction History
        </h2>
        <CustomerTransactionsTable transactions={customerData.transactions} />
      </div>
    </div>
  );
};

export default CustomerDetailView;
